import { ArrayUtil } from "./ArrayUtil";

export class ValueDisplayValue {
    private _value: string;
    private _displayValue: string;
    private _addlValues: Map<string, any>;

    constructor(value: string, displayValue?: string, addlValues?: any|Map<string, any>) {
        this.value = value;
        this.displayValue = displayValue;
        this.loadAddlValues(addlValues);
    }

    get value(): string {
        return this._value;
    }

    set value(value: string) {
        this._value = value;
    }

    get displayValue(): string {
        return this._displayValue ?? this.value;
    }

    set displayValue(value: string) {
        this._displayValue = value;
    }

    public equals(otherVDV: ValueDisplayValue): boolean {
        if (otherVDV == null)
            return false;
        return this.value === otherVDV.value;
    }

    private loadAddlValues(source: any|Map<string, any>) {
        if (source != null) {
            if (source instanceof Map)
                source.forEach((value, key) => this.setAddlValue(key, value))
            else
                Object.entries(source).forEach(([key, value]) => this.setAddlValue(key, value));
        }
    }

    private initAddlValues() {
        if (this._addlValues == null)
            this._addlValues = new Map();
    }

    public getAddlValue(key: string) {
        return this._addlValues?.get(key);
    }

    public getAddlValues(): Map<string, any> {
        return this._addlValues;
    }

    public setAddlValue(key: string, value: any): ValueDisplayValue {
        this.initAddlValues();
        this._addlValues.set(key, value);
        return this;
    }

    public copyAddlValues(sourceVDV: ValueDisplayValue) {
        // Guard against when DropdownItemResolver provides a plain object (which comes from layout deserialization)
        if (sourceVDV != null && sourceVDV instanceof ValueDisplayValue) {
            sourceVDV.getAddlValues()?.forEach((value: any, key: string) => this.setAddlValue(key, value));
        }
    }

    public static getValuesAsString(array: ValueDisplayValue[], delimiter: string = "|"): string {
        return array?.map(vdv => vdv.value).join(delimiter) ?? "";
    }

    public static getDisplayValuesAsString(array: ValueDisplayValue[], delimiter: string = ", "): string {
        return array?.map(vdv => vdv.displayValue).join(delimiter) ?? "";
    }

    public static findByValue(value: string, array: ValueDisplayValue[]): ValueDisplayValue {
        return ArrayUtil.findMatches(array, [value], (item, val) => item.value === val)[0] || null;
    }

    public static findMatchingValues(values: string[], array: ValueDisplayValue[]): ValueDisplayValue[] {
        return ArrayUtil.findMatches(array, values, (item, val) => item.value === val);
    }

    public static findByDisplayValue(displayValue: string, array: ValueDisplayValue[]): ValueDisplayValue {
        return ArrayUtil.findMatches(array, [displayValue], (item, val) => item.displayValue === val)[0] || null;
    }

    public static findMatches(source: ValueDisplayValue[], input: ValueDisplayValue[]): ValueDisplayValue[] {
        return ArrayUtil.findMatches(source, input, (item, val) => item.equals(val));
    }

    /**
     * Implement toJSON so that we only serialize the parts of the object that need to be saved in a layout file.
     * @returns any
     */
    public toJSON(): any {
        const result: any = {
            value: this.value,
            displayValue: this.displayValue
        };
        if (this.getAddlValues()?.size > 0) {
            const nonNullValues = {};
            this.getAddlValues().forEach((value, key) => {
                if (value != null)
                    nonNullValues[key] = value;
            });
            if (Object.keys(nonNullValues).length > 0)
                result.addlValues = nonNullValues;
        }
        return result;
    }
}
